import { buildComponent } from '#components'

import './announcement-bar.css'

type AutoplayPlayer = EventTarget & {
  start: () => void
  stop: () => void
}

type AnnouncementBarCarouselEl = HTMLElement & {
  player?: AutoplayPlayer
}

const selectors = {
  carousel: 'announcement-bar-carousel',
  toggleBtn: '[data-announcement-bar-autoplay-toggle]',
  labelEl: '[data-announcement-bar-autoplay-toggle-label]',
  playIcon: '[data-announcement-bar-autoplay-toggle-icon-play]',
  pauseIcon: '[data-announcement-bar-autoplay-toggle-icon-pause]'
}

const carouselTabIndexObservers = new WeakMap<HTMLElement, MutationObserver>()

const AnnouncementBar = buildComponent({
  name: 'announcement-bar-component',
  onConnected: (component) => {
    const carousel = component.querySelector(selectors.carousel) as AnnouncementBarCarouselEl | null
    const toggleBtn = component.querySelector(selectors.toggleBtn) as HTMLButtonElement | null

    if (!carousel || !toggleBtn) return

    const removeCarouselTabIndex = () => {
      if (carousel.hasAttribute('tabindex')) {
        carousel.removeAttribute('tabindex')
      }
    }

    removeCarouselTabIndex()

    const carouselTabIndexObserver = new MutationObserver(removeCarouselTabIndex)

    carouselTabIndexObserver.observe(carousel, { attributes: true, attributeFilter: ['tabindex'] })
    carouselTabIndexObservers.set(component, carouselTabIndexObserver)

    const labelEl = toggleBtn.querySelector(selectors.labelEl) as HTMLElement | null
    const playIcon = toggleBtn.querySelector(selectors.playIcon) as HTMLElement | null
    const pauseIcon = toggleBtn.querySelector(selectors.pauseIcon) as HTMLElement | null

    const setUi = (isPlaying: boolean) => {
      toggleBtn.setAttribute('aria-pressed', isPlaying ? 'true' : 'false')
      toggleBtn.classList.toggle('is-playing', isPlaying)

      if (labelEl) labelEl.textContent = isPlaying ? 'Pause announcements' : 'Play announcements'
      if (playIcon) playIcon.hidden = isPlaying
      if (pauseIcon) pauseIcon.hidden = !isPlaying
    }

    setUi(true)

    const bindPlayer = (attempt = 0) => {
      const player = carousel.player

      if (!player) {
        if (attempt < 10) requestAnimationFrame(() => bindPlayer(attempt + 1))

        return
      }

      player.addEventListener('player:start', () => setUi(true))
      player.addEventListener('player:stop', () => setUi(false))
    }

    bindPlayer()

    toggleBtn.addEventListener('click', () => {
      const player = carousel.player
      const isPlaying = toggleBtn.getAttribute('aria-pressed') === 'true'

      if (!player) return

      if (isPlaying) {
        player.stop()
      } else {
        player.start()
      }
    })
  },
  onDisconnected: (component) => {
    carouselTabIndexObservers.get(component)?.disconnect()
    carouselTabIndexObservers.delete(component)
  }
})

declare global {
  interface HTMLElementTagNameMap {
    'announcement-bar-component': HTMLElement
  }
}

export default AnnouncementBar
